/**
 * Currency utility functions
 * Provides currency symbols and formatting based on the current language
 */

import i18n from "./i18n";

/**
 * Get the currency symbol based on current language
 * @returns Currency symbol ($ for English, € for French/Spanish)
 */
export function getCurrencySymbol(): string {
  const language = i18n.language || "fr";

  // English uses USD ($)
  if (language.startsWith("en")) {
    return "$";
  }

  // French and Spanish use EUR (€)
  return "€";
}

/**
 * Get the currency code based on current language
 * @returns Currency code (USD for English, EUR for French/Spanish)
 */
export function getCurrencyCode(): string {
  const language = i18n.language || "fr";

  if (language.startsWith("en")) {
    return "USD";
  }

  return "EUR";
}

/**
 * Format a number as currency based on current language
 * @param value - The numeric value to format (handles string values too)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number | string,
  decimals: number = 2,
): string {
  const symbol = getCurrencySymbol();
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return `${symbol}0.${"0".repeat(decimals)}`;
  }
  const formatted = numValue.toFixed(decimals);
  return `${symbol}${formatted}`;
}

/**
 * Get currency label for charts/tables (e.g., "Value ($)" or "Value (€)")
 * @param label - The base label (e.g., "Value")
 * @returns Label with currency symbol
 */
export function getCurrencyLabel(label: string): string {
  const symbol = getCurrencySymbol();
  return `${label} (${symbol})`;
}
